/* Cross-browser support loaded first on every storefront page (layout/theme.liquid
 * and layout/trc-order-editor.liquid, ahead of the other deferred scripts).
 * Targets: Safari/iOS 14+, Firefox ESR 115+, Chrome/Edge 90+, Samsung Internet 15+
 * and in-app browsers. Keep it small, dependency free and ES2017.
 *
 *  - HTMLFormElement.prototype.requestSubmit (Safari/iOS < 16): runs the real
 *    submit path (submit event, capture handlers, constraint validation).
 *  - The formdata event (Safari < 15): new FormData(form) fires it, so the deck
 *    handlers that set the signed Decklist / Companion / variant still run.
 *  - TRCCompat.friendlyErrorMessage: plain copy for raw browser network errors.
 *  - TRCCompat.isInAppBrowser + html.trc-in-app-browser: in-app browsers
 *    (Instagram, Facebook, TikTok and others) block sign-in popups.
 *  - TRCCompat.confirm: an inline, accessible replacement for window.confirm,
 *    which some in-app browsers silently suppress.
 */
(function () {
  'use strict';
  var TRCCompat = window.TRCCompat = window.TRCCompat || {};

  // --- requestSubmit -------------------------------------------------------
  function isSubmitButton(element) {
    var type = String(element.type || '').toLowerCase();
    if (element.localName === 'button') return type === 'submit';
    return element.localName === 'input' && (type === 'submit' || type === 'image');
  }
  function requestSubmit(submitter) {
    var form = this;
    if (submitter != null) {
      if (!(submitter instanceof HTMLElement)) {
        throw new TypeError("Failed to execute 'requestSubmit' on 'HTMLFormElement': parameter 1 is not of type 'HTMLElement'.");
      }
      if (!isSubmitButton(submitter)) {
        throw new TypeError("Failed to execute 'requestSubmit' on 'HTMLFormElement': The specified element is not a submit button.");
      }
      if (submitter.form !== form) {
        throw new DOMException("Failed to execute 'requestSubmit' on 'HTMLFormElement': The specified element is not owned by this form element.", 'NotFoundError');
      }
      // Clicking the submitter runs the browser's own submit algorithm: the
      // submit event (with capture handlers), validation and its name/value.
      // It also runs the button's own click handlers, which native
      // requestSubmit does not; accepted (the theme's handlers expect a press).
      if (!submitter.disabled) { submitter.click(); return; }
    }
    // No submitter (or a disabled one, which ignores clicks): a temporary
    // hidden submit button that carries the submitter's form overrides. A
    // disabled submitter adds no name/value to the form data (as natively).
    var button = document.createElement('input');
    button.type = 'submit';
    button.hidden = true;
    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');
    if (submitter) {
      var copied = ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'];
      if (!submitter.disabled) copied.push('name', 'value');
      copied.forEach(function (name) {
        if (submitter.hasAttribute(name)) button.setAttribute(name, submitter.getAttribute(name));
      });
    }
    form.appendChild(button);
    try { button.click(); } finally { if (button.parentNode) button.parentNode.removeChild(button); }
  }
  TRCCompat.requestSubmit = requestSubmit;
  if (window.HTMLFormElement && typeof HTMLFormElement.prototype.requestSubmit !== 'function') {
    Object.defineProperty(HTMLFormElement.prototype, 'requestSubmit', {
      configurable: true, writable: true, enumerable: false, value: requestSubmit
    });
  }

  // --- formdata event --------------------------------------------------------
  // Browsers without the event (Safari 14) build FormData silently. Fire it
  // from the constructor, as the spec does, so handlers that edit
  // event.formData run. They use set()/delete(), so running twice is harmless.
  function installFormDataEvent() {
    var NativeFormData = window.FormData;
    // Either sign of native support: firing it again would run handlers twice.
    if (typeof NativeFormData !== 'function' || typeof window.FormDataEvent === 'function' || 'onformdata' in document.createElement('form')) return false;
    var building = [];
    var FormDataWithEvent = function FormData(form) {
      var data = arguments.length ? new NativeFormData(form) : new NativeFormData();
      // A handler that builds FormData from the same form gets no nested event.
      if (form instanceof HTMLFormElement && building.indexOf(form) < 0) {
        var event;
        try { event = new Event('formdata', { bubbles: true }); } catch (_) { event = document.createEvent('Event'); event.initEvent('formdata', true, false); }
        Object.defineProperty(event, 'formData', { value: data, enumerable: true });
        building.push(form);
        try { form.dispatchEvent(event); } finally { building.splice(building.indexOf(form), 1); }
      }
      return data;
    };
    FormDataWithEvent.prototype = NativeFormData.prototype;
    try { Object.setPrototypeOf(FormDataWithEvent, NativeFormData); } catch (_) {}
    window.FormData = FormDataWithEvent;
    return true;
  }
  TRCCompat.formDataEventPolyfilled = installFormDataEvent();

  // --- Friendly network errors ---------------------------------------------
  // Raw fetch failures differ per browser ("Load failed" in Safari, "Failed to
  // fetch" in Chrome, "NetworkError when attempting to fetch resource." in
  // Firefox). Shoppers see one plain sentence instead.
  var NETWORK_MESSAGE = 'We could not connect. Check your internet connection and try again.';
  var SECURITY_MESSAGE = 'We couldn\u2019t confirm you\u2019re a person. Please try again.';
  var TIMEOUT_MESSAGE = 'This is taking too long. Check your internet connection and try again.';
  var REPLY_MESSAGE = 'Something went wrong on our end. Please try again.';
  var REPLY_ERROR = /did not match the expected pattern|JSON\.parse|Unexpected token .* JSON|is not valid JSON|Unexpected end of JSON input/i;
  var NETWORK_ERROR = /^(?:type ?error:\s*)?(?:load failed|failed to fetch|fetch failed|networkerror when attempting to fetch resource\.?|network ?error|network request failed|the network connection was lost\.?|the internet connection appears to be offline\.?|a server with the specified hostname could not be found\.?|could not connect to the server\.?|cancelled)$/i;
  var ABORT_ERROR = /^(?:(?:abort|timeout)error:\s*)?(?:the operation was aborted\.?|this operation was aborted\.?|the user aborted a request\.?|signal is aborted without reason|aborted|the operation timed out\.?|the request timed out\.?|request timed out\.?)$/i;
  function friendlyErrorMessage(error, fallback) {
    var name = error && typeof error === 'object' ? String(error.name || '') : '';
    var message = String(error && typeof error === 'object' ? (error.message || '') : (error || '')).trim();
    if (name === 'AbortError' || name === 'TimeoutError' || ABORT_ERROR.test(message)) return TIMEOUT_MESSAGE;
    if (NETWORK_ERROR.test(message)) return NETWORK_MESSAGE;
    // A reply that is not JSON (an error page from a network filter or captive portal).
    if (name === 'SyntaxError' || REPLY_ERROR.test(message)) return REPLY_MESSAGE;
    // An older upload service's raw security check reply.
    if (/turnstile/i.test(message)) return SECURITY_MESSAGE;
    return message || fallback || NETWORK_MESSAGE;
  }
  TRCCompat.friendlyErrorMessage = friendlyErrorMessage;
  TRCCompat.NETWORK_MESSAGE = NETWORK_MESSAGE;
  TRCCompat.TIMEOUT_MESSAGE = TIMEOUT_MESSAGE;

  // --- In-app browsers -------------------------------------------------------
  // Their embedded web views block the sign-in popups that Dropbox and Google
  // need (Google refuses embedded sign-in outright).
  var IN_APP_UA = /\b(?:Instagram|FBAN|FBAV|FB_IAB|FBIOS|FB4A|musical_ly|BytedanceWebview|TikTok|trill_\d|Snapchat|Pinterest|LinkedInApp|TwitterAndroid|Twitter for iP(?:hone|ad))\b|\bLine\/\d|; wv\)/;
  function isInAppBrowser(userAgent) {
    var ua = String(userAgent == null ? (navigator.userAgent || '') : userAgent);
    if (IN_APP_UA.test(ua)) return true;
    // An iOS web view without the Safari token (every iOS browser app,
    // Chrome and Firefox included, keeps "Safari/").
    return /\b(?:iPhone|iPad|iPod)\b/.test(ua) && /AppleWebKit\//.test(ua) && !/\bSafari\//.test(ua);
  }
  TRCCompat.isInAppBrowser = isInAppBrowser;
  TRCCompat.inAppBrowser = isInAppBrowser();
  if (TRCCompat.inAppBrowser) document.documentElement.classList.add('trc-in-app-browser');

  // --- Inline confirmation -----------------------------------------------------
  // confirm({ anchor, message, confirmLabel, cancelLabel, destructive }) shows a
  // small inline panel after the anchor and resolves true or false. Focus moves
  // into it (Cancel for destructive actions), Escape cancels, and focus returns
  // to the element that had it.
  var confirmCount = 0;
  function confirmInline(options) {
    options = options || {};
    var anchor = options.anchor;
    if (!anchor || !anchor.parentNode) {
      return Promise.resolve(typeof window.confirm === 'function' ? !!window.confirm(options.message || '') : false);
    }
    var existing = anchor.parentNode.querySelector('[data-trc-confirm]');
    if (existing && existing.trcConfirmCancel) existing.trcConfirmCancel();
    return new Promise(function (resolve) {
      var id = 'trcConfirm' + (++confirmCount);
      var panel = document.createElement('div');
      panel.className = 'trc-inline-confirm';
      panel.setAttribute('data-trc-confirm', '');
      panel.setAttribute('role', 'alertdialog');
      panel.setAttribute('aria-modal', 'false');
      panel.setAttribute('aria-labelledby', id + 'Message');
      panel.style.cssText = 'margin:1.2rem 0;padding:1.4rem 1.6rem;border:1px solid #d0d5dd;border-radius:10px;background:#fff;color:#101828;font-size:1.4rem;line-height:1.5;grid-column:1/-1';
      var text = document.createElement('p');
      text.id = id + 'Message';
      text.style.cssText = 'margin:0 0 1.2rem';
      text.textContent = options.message || '';
      var actions = document.createElement('div');
      // Margins, not flex gap (iOS 14.0 to 14.4 has no flex gap).
      actions.style.cssText = 'display:flex;flex-wrap:wrap;margin:-.5rem';
      function button(label, primary) {
        var element = document.createElement('button');
        element.type = 'button';
        element.textContent = label;
        element.style.cssText = 'margin:.5rem;padding:1rem 1.6rem;border-radius:8px;border:1px solid ' + (primary ? '#101828;background:#101828;color:#fff' : '#d0d5dd;background:#fff;color:#101828') + ';font:inherit;font-weight:600;cursor:pointer;text-transform:none;letter-spacing:normal';
        return element;
      }
      var confirmButton = button(options.confirmLabel || 'Continue', true);
      var cancelButton = button(options.cancelLabel || 'Cancel', false);
      actions.appendChild(confirmButton);
      actions.appendChild(cancelButton);
      panel.appendChild(text);
      panel.appendChild(actions);
      var returnFocus = document.activeElement;
      var settled = false;
      function finish(value, keepFocus) {
        if (settled) return;
        settled = true;
        // Closed from outside (its deck changed): focus stays where the shopper is,
        // unless it was inside the panel.
        var inside = panel.contains(document.activeElement);
        panel.removeEventListener('keydown', onKeydown);
        if (panel.parentNode) panel.parentNode.removeChild(panel);
        var target = returnFocus && returnFocus.isConnected ? returnFocus : anchor;
        if ((!keepFocus || inside) && target && target.isConnected && typeof target.focus === 'function' && options.restoreFocus !== false) {
          try { target.focus(); } catch (_) {}
        }
        resolve(value);
      }
      function onKeydown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') { event.preventDefault(); event.stopPropagation(); finish(false); }
      }
      panel.trcConfirmCancel = function () { finish(false); };
      confirmButton.addEventListener('click', function () { finish(true); });
      cancelButton.addEventListener('click', function () { finish(false); });
      panel.addEventListener('keydown', onKeydown);
      if (options.signal) {
        if (options.signal.aborted) { finish(false); return; }
        options.signal.addEventListener('abort', function () { finish(false, true); });
      }
      anchor.parentNode.insertBefore(panel, anchor.nextSibling);
      (options.destructive ? cancelButton : confirmButton).focus();
    });
  }
  TRCCompat.confirm = confirmInline;
})();
